import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cog, Gauge, Users, Plane, Cpu, ChevronRight, Star } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { FLEET } from "../data";

const specIcon = { Engine: Cog, Cruise: Gauge, Seating: Users, Avionics: Cpu, Uses: Plane };

export const Fleet = ({ showHeader = true }) => {
  const [active, setActive] = useState(0);
  const a = FLEET[active];
  return (
    <section id="fleet" className="py-24 lg:py-32 bg-white" data-testid="fleet">
      <div className="max-w-7xl mx-auto px-6">
        {showHeader && (
          <div className="max-w-2xl mb-16">
            <Reveal><Overline>The Fleet</Overline></Reveal>
            <Reveal delay={0.1}><h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight text-[var(--primary)]">A proven Cessna 172 fleet.</h2></Reveal>
            <Reveal delay={0.15}><p className="mt-5 text-[var(--muted)] text-base md:text-lg">As {"Africa's"} certified Cessna Pilot Center, we train exclusively on the Cessna 172 — the {"world's"} most trusted trainer — including G1000-equipped glass-cockpit aircraft such as <span className="text-accent font-semibold">ZS-PWW</span>, fitted with Garmin G1000 avionics for hour building and instrument training.</p></Reveal>
          </div>
        )}
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="rounded-2xl overflow-hidden card-shadow-lg bg-navy">
                <div className="relative aspect-[16/10]">
                  <AnimatePresence mode="wait">
                    <motion.div key={a.reg} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0">
                      {a.type === "video" ? (
                        <video autoPlay muted loop playsInline className="w-full h-full object-cover"><source src={a.media} type="video/mp4" /></video>
                      ) : (
                        <img src={a.media} alt={a.name} className="w-full h-full object-cover" />
                      )}
                    </motion.div>
                  </AnimatePresence>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f3159]/90 via-[#164275]/10 to-transparent" />
                  {a.featured && <div className="absolute top-5 left-5 btn-primary px-4 py-1.5 text-xs inline-flex items-center gap-1.5"><Star size={13} /> Glass Cockpit</div>}
                  <div className="absolute bottom-0 left-0 p-6 sm:p-8 w-full">
                    <span className="overline text-accent-2">{a.reg} · {a.tag}</span>
                    <h3 className="mt-2 font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-white">{a.name}</h3>
                    <div className="mt-6 hidden sm:grid sm:grid-cols-5 gap-3">
                      {Object.entries(a.specs).map(([k, v]) => {
                        const I = specIcon[k] || Plane;
                        return (
                          <div key={k} className="glass rounded-xl p-3">
                            <I size={15} className="text-accent-2" />
                            <div className="mt-1.5 text-[10px] uppercase tracking-wide text-white/50">{k}</div>
                            <div className="text-white text-[11px] font-medium leading-tight mt-0.5">{v}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 p-5 sm:hidden">
                  {Object.entries(a.specs).map(([k, v]) => {
                    const I = specIcon[k] || Plane;
                    return (
                      <div key={k} className="glass rounded-xl p-3">
                        <I size={15} className="text-accent-2" />
                        <div className="mt-1.5 text-[10px] uppercase tracking-wide text-white/50">{k}</div>
                        <div className="text-white text-[11px] font-medium leading-tight mt-0.5">{v}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-4">
            {FLEET.map((f, i) => (
              <Reveal key={f.reg} delay={i * 0.08}>
                <button onClick={() => setActive(i)} data-testid={`fleet-item-${i}`}
                  className={`w-full text-left rounded-2xl p-5 border transition-all duration-300 flex items-center gap-4 ${i === active ? "bg-navy border-[var(--primary)]" : "bg-white border-[var(--line)] hover:border-accent"}`}>
                  <div className={`h-16 w-20 rounded-lg overflow-hidden shrink-0 ${i === active ? "" : ""}`}>
                    {f.type === "video" ? <video muted loop autoPlay playsInline className="h-full w-full object-cover"><source src={f.thumb || f.media} type="video/mp4" /></video> : <img src={f.media} alt="" className="h-full w-full object-cover" loading="lazy" />}
                  </div>
                  <div className="flex-1">
                    <div className={`font-display font-semibold ${i === active ? "text-white" : "text-[var(--primary)]"}`}>{f.reg}</div>
                  </div>
                  <ChevronRight size={18} className={i === active ? "text-accent-2" : "text-[var(--grey-soft)]"} />
                </button>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <p className="text-xs text-[var(--muted)] leading-relaxed px-1">{a.desc}</p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
